import React, { useState, useCallback, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from '@stripe/react-stripe-js';
import { supabase } from '../lib/supabaseClient';

const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = STRIPE_PUBLISHABLE_KEY ? loadStripe(STRIPE_PUBLISHABLE_KEY) : null;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:8000';

/**
 * Inline paywall modal — shows pricing card, then Stripe embedded checkout.
 * Props:
 *   isOpen        boolean — controls visibility
 *   onClose       () => void — called when user dismisses
 *   onSuccess     () => void — called after payment confirmed (parent handles polling)
 */
export default function PaywallModal({ isOpen, onClose, onSuccess }) {
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);
  const [restoreState, setRestoreState] = useState(null); // null | 'loading' | 'granted' | 'not_found'
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [codeState, setCodeState] = useState(null); // null | 'loading' | 'granted' | 'invalid'

  // Reset to pricing view when reopened
  useEffect(() => {
    if (!isOpen) {
      setShowCheckout(false);
      setCheckoutError(null);
      setRestoreState(null);
      setShowCodeInput(false);
      setPromoCode('');
      setCodeState(null);
    }
  }, [isOpen]);

  const handleRestoreAccess = async () => {
    setRestoreState('loading');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) { setRestoreState('not_found'); return; }
      const res = await fetch(`${BACKEND_URL}/api/billing/restore-access`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.has_lifetime_access) {
        setRestoreState('granted');
        setTimeout(() => { onSuccess?.(); onClose(); }, 1500);
      } else {
        setRestoreState('not_found');
      }
    } catch {
      setRestoreState('not_found');
    }
  };

  const handleRedeemCode = async () => {
    if (!promoCode.trim()) return;
    setCodeState('loading');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) { setCodeState('invalid'); return; }
      const res = await fetch(`${BACKEND_URL}/api/billing/redeem-code`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: promoCode.trim() }),
      });
      if (res.ok) {
        setCodeState('granted');
        setTimeout(() => { onSuccess?.(); onClose(); }, 1500);
      } else {
        setCodeState('invalid');
      }
    } catch {
      setCodeState('invalid');
    }
  };

  const fetchClientSecret = useCallback(async () => {
    setCheckoutError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('Not authenticated');

      const res = await fetch(`${BACKEND_URL}/api/billing/create-lifetime-checkout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to create checkout session');
      const data = await res.json();
      return data.client_secret;
    } catch (err) {
      setCheckoutError(err.message || 'Something went wrong. Please try again.');
      throw err;
    }
  }, []);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      onClick={(e) => { if (e.target === e.currentTarget && !showCheckout) onClose(); }}
    >
      {/* Backdrop — only closes modal on pricing screen, not mid-checkout */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={() => { if (!showCheckout) onClose(); }}
      />

      {/* Card */}
      <div className="relative z-10 bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {!showCheckout ? (
          /* Pricing panel */
          <div className="p-8 flex flex-col items-center text-center">
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-gray-300 hover:text-gray-500 transition-colors"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <p className="text-xs font-semibold tracking-widest uppercase mb-3" style={{ color: '#007AFF' }}>
              Founding Member Offer
            </p>
            <h2 className="text-2xl font-bold text-gray-900 mb-1" style={{ fontFamily: "'Montserrat', sans-serif", letterSpacing: '-0.02em' }}>
              Unlock Your Full Roadmap
            </h2>
            <p className="text-gray-500 text-sm mb-6">
              Get lifetime access to all roles, paths, and AI-powered career guidance.
            </p>

            {/* Pricing */}
            <div className="flex items-center gap-4 mb-8">
              <div className="relative inline-block leading-none">
                <span className="text-4xl font-extrabold text-gray-300">$12</span>
                <svg
                  className="absolute inset-0 w-full h-full pointer-events-none overflow-visible"
                  preserveAspectRatio="none"
                >
                  <line
                    x1="4%" y1="92%"
                    x2="96%" y2="8%"
                    stroke="#d1d5db"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
              <span className="text-3xl font-bold" style={{ color: '#007AFF' }}>$7.98</span>
            </div>

            {/* Feature list */}
            <ul className="text-left text-sm text-gray-600 space-y-2 mb-8 w-full">
              {[
                'Full personalized career roadmap',
                'AI-powered role suggestions & paths',
                'What-if career scenario editor',
                'Role deep-dives & salary data',
                'All future updates included',
              ].map((f) => (
                <li key={f} className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-green-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                  {f}
                </li>
              ))}
            </ul>

            <button
              onClick={() => setShowCheckout(true)}
              className="w-full py-3 rounded-xl text-white font-semibold text-base transition-colors"
              style={{ backgroundColor: '#007AFF' }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = '#0066D6'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = '#007AFF'}
            >
              Get Access for $7.98
            </button>
            <p className="text-xs text-gray-400 mt-3">Secure payment powered by Stripe</p>

            {/* Restore access for users who already paid */}
            <div className="mt-4 pt-4 border-t border-gray-100 w-full text-center">
              {restoreState === 'granted' ? (
                <p className="text-xs text-green-600 font-semibold">Payment confirmed, unlocking your access...</p>
              ) : restoreState === 'not_found' ? (
                <p className="text-xs text-gray-500">No payment found for this account. Email <span className="font-semibold text-gray-700">support@figcareer.com</span> and we'll sort it out.</p>
              ) : (
                <button
                  onClick={handleRestoreAccess}
                  disabled={restoreState === 'loading'}
                  className="text-xs text-gray-400 hover:text-blue-500 transition-colors disabled:opacity-50"
                >
                  {restoreState === 'loading' ? 'Checking payment...' : 'Already paid? Restore access'}
                </button>
              )}

              {/* Access code — shown only after clicking the toggle, not visible by default */}
              {restoreState !== 'granted' && (
                <div className="mt-2">
                  {!showCodeInput ? (
                    <button
                      onClick={() => setShowCodeInput(true)}
                      className="text-xs text-gray-300 hover:text-gray-400 transition-colors"
                    >
                      Have an access code?
                    </button>
                  ) : (
                    <div className="mt-2 flex flex-col items-center gap-2">
                      {codeState === 'granted' ? (
                        <p className="text-xs text-green-600 font-semibold">Access granted, unlocking...</p>
                      ) : (
                        <>
                          <div className="flex gap-2 w-full max-w-xs">
                            <input
                              type="text"
                              value={promoCode}
                              onChange={e => { setPromoCode(e.target.value.toUpperCase()); setCodeState(null); }}
                              onKeyDown={e => e.key === 'Enter' && handleRedeemCode()}
                              placeholder="Enter code"
                              className="flex-1 px-3 py-1.5 text-xs border border-gray-200 rounded-lg outline-none focus:border-blue-400 text-gray-700 tracking-widest uppercase"
                            />
                            <button
                              onClick={handleRedeemCode}
                              disabled={codeState === 'loading' || !promoCode.trim()}
                              className="px-3 py-1.5 text-xs rounded-lg text-white font-semibold disabled:opacity-50 transition-colors"
                              style={{ backgroundColor: '#007AFF' }}
                            >
                              {codeState === 'loading' ? '...' : 'Apply'}
                            </button>
                          </div>
                          {codeState === 'invalid' && (
                            <p className="text-xs text-red-400">Invalid or expired code.</p>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          /* Stripe Embedded Checkout */
          <div className="p-4">
            <button
              onClick={() => { setShowCheckout(false); setCheckoutError(null); }}
              className="mb-3 text-sm text-gray-400 hover:text-gray-600 flex items-center gap-1"
            >
              ← Back
            </button>

            {checkoutError ? (
              <div className="py-10 flex flex-col items-center text-center gap-4">
                <svg className="w-10 h-10 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-gray-600">Couldn't load checkout. Please try again.</p>
                <button
                  onClick={() => { setCheckoutError(null); setShowCheckout(false); setTimeout(() => setShowCheckout(true), 50); }}
                  className="px-6 py-2 rounded-xl text-white text-sm font-semibold"
                  style={{ backgroundColor: '#007AFF' }}
                >
                  Try Again
                </button>
              </div>
            ) : !stripePromise ? (
              <div className="py-10 flex flex-col items-center text-center gap-3">
                <p className="text-sm text-gray-600">Checkout is not configured for this environment yet.</p>
                <p className="text-xs text-gray-400">Set REACT_APP_STRIPE_PUBLISHABLE_KEY in your frontend .env to enable payments.</p>
              </div>
            ) : (
              <EmbeddedCheckoutProvider stripe={stripePromise} options={{ fetchClientSecret }}>
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
